import React, { useState, useContext } from 'react';
import { Row, CustomTooltip } from '../../shared/utils';
import { uploadFile } from '../../../API/Api';
import CountryList from './CountryList';
import { toast } from 'react-toastify';
import Tooltip from '@mui/material/Tooltip';
import { jwtDecode } from 'jwt-decode';
import Modal from '../../shared/Modal';
import { DateTime } from 'luxon';
import { TokenContext } from '../../../App';

const ConnectedOrBeneficialParty = ({
  formData,
  disabled,
  setFormData,
  personType,
  formName,
  token,
  isValidEmail,
  setIsValidEmail,
  borderColor,
  mandatoryConnectedFieldError,
  setMandatoryConnectedFieldError,
  listedInRule84,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const decodedToken = jwtDecode(token);

  const formatDateToString = date => {
    if (!date) return '';
    return DateTime.fromISO(date).toFormat('yyyy-MM-dd');
  };

  const handlePartyInputChange = (value, index, field, isCheckbox = false) => {
    const updatedParties = [...formData[formName]];
    updatedParties[index][field] = value;
    // console.log(value, index, field)
    if (isCheckbox) {
      updatedParties[index][field] = !updatedParties[index][field];
    }
    if (field === 'notApplicable') {
      if (value == true) {
        updatedParties[index].identificationExpiryDate = '';
      } else {
        updatedParties[index].identificationExpiryDate = '9999-12-31';
      }
    }
    const newData = {
      ...formData,
      [formName]: updatedParties,
    };
    setFormData(newData);

    let newMandatoryConnectedFieldError = [...mandatoryConnectedFieldError];
    // Initialize error state for the party if it doesn't exist
    if (!newMandatoryConnectedFieldError[index]) {
      newMandatoryConnectedFieldError[index] = {
        name: false,
        address: false,
        identificationNumber: false,
        roleSelected: false,
      };
    }
    // Check if the value for the field is empty
    // 仅对字符串类型检查是否为空
    if (typeof value === 'string') {
      newMandatoryConnectedFieldError[index][field] = value.trim() === '';
    } else {
      // 非字符串（如布尔值）不需要检查空值
      newMandatoryConnectedFieldError[index][field] = false;
    }

    setMandatoryConnectedFieldError(newMandatoryConnectedFieldError);
  };

  const handlePartyFileUpload = async (e, index, field) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    try {
      const data = new FormData();
      data.append('file', file);
      const filename = `${field}_${file.name}`;
      const res = await uploadFile(data, filename, token);
      const updatedParties = [...formData[formName]];
      updatedParties[index][field] = res.data.url;
      setFormData({
        ...formData,
        [formName]: updatedParties,
      });
    } catch (e) {
      console.log('err');
      toast.error('Invalid file type!');
    }
  };

  const handleFileRemove = async (fileName, index) => {
    if (disabled) {
      return;
    }

    const updatedFormData = { ...formData };
    updatedFormData.connectedParty[index][fileName] = '';
    setFormData(updatedFormData);
  };

  const addConnectedParty = () => {
    // if (formData[formName].length > 14) {
    //   toast.error(`Max 15 ${personType} allowed!`)
    //   return
    // }

    setFormData({
      ...formData,
      [formName]: [
        ...formData[formName],
        {
          authorisedSignatory: '',
          ultimateBeneficialOwner: '',
          connectedParty: '',
          name: '',
          gender: '',
          address: '',
          addressCountry: 'SG',
          addressProofURL: '',
          identificationNumber: '',
          identificationDocumentURL: '',
          identificationExpiryDate: '',
          notApplicable: '',
          dateOfBirth: '',
          email: '',
          residenceStatus: '',
          nationality: 'SG',
          contactNumber: '',
          pepPerson: 'no',
          pepPersonCountryOrOrganisation: '',
          pepPersonPublicFunctionNature: '',
          pepPersonRoleFrom: '',
          pepPersonRoleTo: '',
          pepAssociate: 'no',
          pepAssociateRelationship: '',
          pepAssociateName: '',
          pepAssociateCountryOrOrganisation: '',
          pepAssociatePublicFunctionNature: '',
          pepAssociateRoleFrom: '',
          pepAssociateRoleTo: '',
          pepFamily: 'no',
          pepFamilyRelationship: '',
          pepFamilyName: '',
          pepFamilyCountryOrOrganisation: '',
          pepFamilyPublicFunctionNature: '',
          pepFamilyRoleFrom: '',
          pepFamilyRoleTo: '',
          notes: '',
        },
      ],
    });
    setCurrentPage(formData[formName].length);
  };

  const deleteConnectedParty = index => {
    // const deletedPartyList = [...formData[formName]].splice(index, 1); //This gives you back the pop-ed element instead by assigning. like assign pop, vs just conudct pop operation
    const deletedPartyList = [...formData[formName]];
    deletedPartyList.splice(index, 1);

    setFormData({
      ...formData,
      [formName]: deletedPartyList,
    });

    if (currentPage < 2) {
      setCurrentPage(0); // Reset to the first page if all connected parties are deleted
    } else {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleChangePage = index => {
    setCurrentPage(index);
  };

  const partyList = formData[formName];
  // 当 listedInRule84 为 'yes' 时，只有 Full Name 是必填的
  // 如果 listedInRule84 未传递（undefined），则保持原有行为（所有字段都必填）
  const showRequiredIndicator = listedInRule84 !== 'yes';

  return (
    <div className='p-3'>
      <button
        disabled={disabled}
        onClick={addConnectedParty}
        className={`border border-${borderColor}-500 hover:bg-${borderColor}-400 hover:text-white transition duration-200 text-${borderColor}-500 font-medium py-2 px-4 rounded-lg mb-1`}
      >
        + Add {personType}{' '}
      </button>

      {partyList?.map((party, index) => (
        <div key={index} style={{ display: index === currentPage ? 'block' : 'none' }}>
          <div className='flex flex-wrap items-center gap-2'>
            <p className='text-xl font-semibold underline'>
              {' '}
              {personType} {index + 1}{' '}
            </p>
            {partyList.map((_, index) => (
              <button
                key={index}
                onClick={() => handleChangePage(index)}
                className={`border rounded-lg px-2 hover:bg-${borderColor}-400 ${currentPage === index ? `bg-${borderColor}-500` : ''}`}
              >
                {' '}
                {index + 1}{' '}
              </button>
            ))}
            <button
              disabled={disabled}
              onClick={() => deleteConnectedParty(index)}
              className='border border-red-500 hover:bg-red-400 hover:text-white transition duration-200 text-red-500 font-medium py-1.5 px-4 rounded-lg'
            >
              - Delete {personType} {currentPage + 1}{' '}
            </button>
          </div>

          <div className='max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg'>
            {/* 0th row */}
            <div className='grid grid-cols-1 gap-4 mb-4'>
              {/* <div>
                <p className="font-medium mb-1"> Nature of Relationship </p>
                <input maxLength={800} disabled={disabled} type="text" name='relationship' placeholder="Enter Nature of Relationship" value={formData[formName][index]?.relationship} onChange={(e) => handlePartyInputChange(e.target.value, index, 'relationship')} className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500" />
              </div> */}
              <div className='block font-medium border'>
                <label className='mx-4'>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='checkbox'
                    name='authorisedSignatory'
                    checked={formData[formName][index]?.authorisedSignatory}
                    onChange={e =>
                      handlePartyInputChange(
                        formData[formName]?.[index]?.authorisedSignatory,
                        index,
                        'authorisedSignatory',
                        true
                      )
                    }
                    className='w-4 h-4 my-3.5 mx-1 relative top-0.5'
                  />
                  Authorised Signatory
                  <Tooltip
                    title={<p style={{ fontSize: '14px' }}> Person authorised to act on one's behalf. </p>}
                    arrow
                  >
                    <span className='text-xl relative top-0.5'> ⓘ </span>
                  </Tooltip>
                </label>
                <label className='mr-4'>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='checkbox'
                    name='connectedParty'
                    checked={formData[formName][index]?.connectedParty}
                    onChange={e =>
                      handlePartyInputChange(formData[formName]?.[index]?.connectedParty, index, 'connectedParty', true)
                    }
                    className='w-4 h-4 mx-1 relative top-0.5'
                  />
                  Connected Party
                  <Tooltip title={<p style={{ fontSize: '14px' }}> Eg. Company Director </p>} arrow>
                    <span className='text-xl relative top-0.5'> ⓘ </span>
                  </Tooltip>
                </label>
                <label className='mr-4'>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='checkbox'
                    name='ultimateBeneficialOwner'
                    checked={formData[formName][index]?.ultimateBeneficialOwner}
                    onChange={e =>
                      handlePartyInputChange(
                        formData[formName]?.[index]?.ultimateBeneficialOwner,
                        index,
                        'ultimateBeneficialOwner',
                        true
                      )
                    }
                    className='w-4 h-4 mx-1 relative top-0.5'
                  />
                  Ultimate Beneficial Owner
                  <Tooltip
                    title={
                      <p style={{ fontSize: '14px' }}>
                        {' '}
                        Owns more than 25% of the company's shares, or controls more than 25% of the voting rights.{' '}
                      </p>
                    }
                    arrow
                  >
                    <span className='text-xl relative top-0.5'> ⓘ </span>
                  </Tooltip>
                </label>
                {showRequiredIndicator && <i className='text-red-600'>*</i>}
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.roleSelected && (
                  <p style={{ color: 'red' }}>
                    Please select at least one role (Authorised Signatory, Connected Party or Ultimate Beneficial Owner)
                  </p>
                )}
              </div>
            </div>

            {/* 1st row */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              <div>
                <label className='block font-medium text-gray-700 mb-1'>
                  {' '}
                  Full Name <i className='text-red-600'>*</i>
                </label>
                <input
                  maxLength={800}
                  disabled={disabled}
                  type='text'
                  id='fullName'
                  name='name'
                  placeholder='Enter Full Name'
                  value={formData[formName][index]?.name}
                  onChange={e => handlePartyInputChange(e.target.value, index, 'name')}
                  className='w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                />
                {mandatoryConnectedFieldError[index]?.name && <p style={{ color: 'red' }}>Please enter a name</p>}
              </div>
              <div>
                <label className='block font-medium text-gray-700 my-1'>
                  Gender {showRequiredIndicator && <i className='text-red-600'>*</i>}
                </label>
                <div className='mt-1 flex items-center'>
                  {/* // somehow, radiobutton NEEDS that ? to update properly */}
                  <input
                    maxLength={800}
                    disabled={disabled}
                    key={'male' + index}
                    type='radio'
                    id='male'
                    name={'gender' + index}
                    value='male'
                    checked={formData[formName][index]?.gender === 'male'}
                    onChange={e => handlePartyInputChange(e.target.value, index, 'gender')}
                    className='mr-1 w-4 h-4'
                  />
                  {/* <input maxLength={800} disabled={disabled} key={"male"+index} type="radio" id="male" name="gender" value="male" checked={formData[formName][index]?.gender === 'male'} onChange={(e) => handlePartyInputChange(e.target.value, index, 'gender')} className="mr-1 w-4 h-4"/> */}
                  <label className='mr-3'>Male</label>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    key={'female' + index}
                    type='radio'
                    id='female'
                    name={'gender' + index}
                    value='female'
                    checked={formData[formName][index]?.gender === 'female'}
                    onChange={e => handlePartyInputChange(e.target.value, index, 'gender')}
                    className='mr-1 w-4 h-4'
                  />
                  <label>Female</label>
                </div>
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.gender && (
                  <p style={{ color: 'red' }}>Please select the gender.</p>
                )}
              </div>
            </div>

            {/* 2nd row */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              <div>
                <p className='block font-medium text-gray-700 mb-1'>
                  {' '}
                  Residential Address {showRequiredIndicator && <i className='text-red-600'>*</i>}
                </p>
                <input
                  maxLength={800}
                  disabled={disabled}
                  type='text'
                  id='address'
                  name='address'
                  placeholder='Enter Residential Address'
                  value={formData[formName][index].address}
                  onChange={e => handlePartyInputChange(e.target.value, index, 'address')}
                  className='w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                />
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.address && (
                  <p style={{ color: 'red' }}>Please enter Address</p>
                )}
              </div>
              <div>
                <span htmlFor='fileInput' className='block font-medium text-gray-700 m-1.5'>
                  Upload Proof of Address {showRequiredIndicator && <i className='text-red-600'>*</i>}
                </span>
                {formData[formName][index].addressProofURL && (
                  <div className='flex'>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/get-upload?fileName=${formData[formName][index].addressProofURL.split('/').slice(-1)[0]}&accessToken=${token}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 mr-2'
                    >
                      {decodeURIComponent(formData[formName][index].addressProofURL.split('/').slice(-1)[0])}
                    </a>
                    <span className='cursor-pointer' onClick={() => handleFileRemove('addressProofURL', index)}>
                      {' '}
                      ❌{' '}
                    </span>
                  </div>
                )}

                <label className='cursor-pointer bg-gray-200 px-1 py-0.5 mt-1 rounded'>
                  Browse
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='file'
                    id='fileInput'
                    name='addressProofURL'
                    onChange={e => handlePartyFileUpload(e, index, 'addressProofURL')}
                    className='hidden'
                  />
                </label>
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.addressProofURL && (
                  <p style={{ color: 'red' }}>Please Upload Proof of Address</p>
                )}
              </div>
            </div>

            {/* 2.5nd row */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              <div>
                <label className='block font-medium text-gray-700 mb-1'>
                  {' '}
                  Country of Residence{showRequiredIndicator && <i className='text-red-600'>*</i>}
                </label>
                <CountryList
                  width={'w-full'}
                  disabled={disabled}
                  value={formData[formName][index].addressCountry}
                  handleInputChange={e => handlePartyInputChange(e.target.value, index, 'addressCountry')}
                  name='addressCountry'
                />
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.addressCountry && (
                  <p style={{ color: 'red' }}>Please enter Country of Residence</p>
                )}
              </div>
              <div>
                <label htmlFor='nationality' className='block font-medium text-gray-700 mb-1'>
                  Nationality{showRequiredIndicator && <i className='text-red-600'>*</i>}
                </label>
                <CountryList
                  width={'w-full'}
                  disabled={disabled}
                  value={formData[formName][index].nationality}
                  handleInputChange={e => handlePartyInputChange(e.target.value, index, 'nationality')}
                  name='nationality'
                />
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.nationality && (
                  <p style={{ color: 'red' }}>Please choose</p>
                )}
              </div>
            </div>

            {/* 3rd row */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              <div>
                <label htmlFor='identificationNumber' className='block font-medium text-gray-700 mb-1'>
                  Unique Identification Number {showRequiredIndicator && <i className='text-red-600'>*</i>}{' '}
                </label>
                <input
                  maxLength={800}
                  disabled={disabled}
                  type='text'
                  id='identificationNumber'
                  name='identificationNumber'
                  placeholder='Enter Unique Identification Number'
                  value={formData[formName][index].identificationNumber}
                  onChange={e => handlePartyInputChange(e.target.value, index, 'identificationNumber')}
                  className='w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                />
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.identificationNumber && (
                  <p style={{ color: 'red' }}>Please enter Identification Number</p>
                )}
              </div>
              <div>
                <span htmlFor='fileInput' className='block font-medium text-gray-700 mb-1.5'>
                  Upload Identification Document{showRequiredIndicator && <i className='text-red-600'>*</i>}
                </span>
                {formData[formName][index].identificationDocumentURL && (
                  <div className='flex'>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/get-upload?fileName=${formData[formName][index].identificationDocumentURL.split('/').slice(-1)[0]}&accessToken=${token}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 mr-2'
                    >
                      {decodeURIComponent(formData[formName][index].identificationDocumentURL.split('/').slice(-1)[0])}
                    </a>
                    <span
                      className='cursor-pointer'
                      onClick={() => handleFileRemove('identificationDocumentURL', index)}
                    >
                      {' '}
                      ❌{' '}
                    </span>
                  </div>
                )}

                <label className='cursor-pointer bg-gray-200 px-1 py-0.5 mt-1 rounded'>
                  Browse
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='file'
                    id='fileInput'
                    name='identificationDocumentURL'
                    onChange={e => handlePartyFileUpload(e, index, 'identificationDocumentURL')}
                    className='hidden'
                  />
                </label>
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.identificationDocumentURL && (
                  <p style={{ color: 'red' }}>Please Upload Identification Document</p>
                )}
              </div>
            </div>

            {/* 4th and 5th row */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              <div>
                <label htmlFor='expiryDate' className='block font-medium text-gray-700 mb-1'>
                  Expiry Date of Identification Document{showRequiredIndicator && <i className='text-red-600'>*</i>}
                </label>
                <input
                  maxLength={800}
                  disabled={disabled || formData[formName][index]?.notApplicable}
                  type='date'
                  id='expiryDate'
                  value={formData[formName][index].identificationExpiryDate}
                  name='identificationExpiryDate'
                  onChange={e => handlePartyInputChange(e.target.value, index, 'identificationExpiryDate')}
                  className='w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                  style={formData[formName][index]?.notApplicable ? { color: 'transparent' } : {}}
                />
                <label className='mt-4 flex items-center'>
                  <span className='block font-medium text-gray-700 mr-1'>Tick if not applicable</span>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='checkbox'
                    name='notApplicable'
                    checked={formData[formName][index]?.notApplicable}
                    onChange={e =>
                      handlePartyInputChange(formData[formName]?.[index]?.notApplicable, index, 'notApplicable', true)
                    }
                    className='w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500'
                  />
                </label>
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.identificationExpiryDate && (
                  <p style={{ color: 'red' }}>Please Choose</p>
                )}
              </div>
              <div>
                <label htmlFor='dob' className='block font-medium text-gray-700 mb-1'>
                  Date of Birth{showRequiredIndicator && <i className='text-red-600'>*</i>}
                </label>
                <input
                  maxLength={800}
                  disabled={disabled}
                  type='date'
                  id='dob'
                  value={formData[formName][index].dateOfBirth}
                  onChange={e => handlePartyInputChange(e.target.value, index, 'dateOfBirth')}
                  name='dateOfBirth'
                  className='w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                />
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.dateOfBirth && (
                  <p style={{ color: 'red' }}>Please Choose Date of Birth</p>
                )}
              </div>
            </div>

            {/* 6th Row */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              <div>
                <label className='block font-medium text-gray-700 mb-1'>Contact Number</label>
                <input
                  maxLength={800}
                  disabled={disabled}
                  type='tel'
                  placeholder='Enter Contact Number'
                  name='contactNumber'
                  value={formData[formName][index].contactNumber}
                  onChange={e => handlePartyInputChange(e.target.value, index, 'contactNumber')}
                  className='w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                />
              </div>
              <div>
                <label className='block font-medium text-gray-700 mb-1'>Email</label>
                <input
                  maxLength={800}
                  disabled={disabled}
                  type='email'
                  id='email'
                  placeholder='Enter Email'
                  value={formData[formName][index].email}
                  onChange={e => handlePartyInputChange(e.target.value, index, 'email')}
                  name='email'
                  className='w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                />
                {/* {!isValidEmail && <p style={{ color: 'red' }}>Please enter a valid email address</p>} */}
              </div>
            </div>

            {/* 7th row */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              {/* <div>
                <label htmlFor="residenceStatus" className="block font-medium text-gray-700 mb-1">Residence Status</label>
                <select id="residenceStatus" value={formData[formName].residenceStatus} onChange={(e) => handlePartyInputChange(e.target.value, index, 'residenceStatus')} name='residenceStatus'className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500">
                  <option value=""> - Please Select - </option>
                  <option value="singaporean">Singaporean</option>
                  <option value="permanentResident">Permanent Resident</option>
                  <option value="foreigner">Foreigner</option>
                  <option value="workPermit">Work Permit</option>
                </select>
              </div> */}
            </div>

            {/* 9th row */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              <div className={formData[formName][index].pepPerson === 'yes' ? 'border p-2' : 'p-2'}>
                <div className='block'>
                  <label className='font-medium text-gray-700'>
                    Are you a current or former PEP?
                    {showRequiredIndicator && <i className='text-red-600 ml-1 mr-1'>*</i>}
                  </label>
                  <Modal
                    title='Definitions'
                    token={token}
                    className='inline'
                    CustomButton={() => <CustomTooltip title='Click to View definition/guidance' />}
                  >
                    <CurrentPEP />
                  </Modal>
                </div>
                <div className='mt-1 flex items-center'>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='radio'
                    name={'pepPerson' + index}
                    value='yes'
                    onChange={e => handlePartyInputChange(e.target.value, index, 'pepPerson')}
                    checked={formData[formName][index]?.pepPerson === 'yes'}
                    className='mr-1 w-4 h-4'
                  />
                  <label className='mr-2'>Yes</label>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='radio'
                    name={'pepPerson' + index}
                    value='no'
                    onChange={e => handlePartyInputChange(e.target.value, index, 'pepPerson')}
                    checked={formData[formName][index]?.pepPerson === 'no'}
                    className='mr-1 w-4 h-4'
                  />
                  <label>No</label>
                </div>
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.pepPerson && (
                  <p style={{ color: 'red' }}>Please choose</p>
                )}

                {formData[formName][index].pepPerson === 'yes' && (
                  <div className='mt-1 '>
                    <CustomTooltip
                      className='block'
                      title='Please describe the nature of your Political Exposure, including your official title or role, the government/institution you are affiliated with, the country of service, and the period of involvement. e.g. Former Deputy Minister, Ministry of Health, Malaysia (2015–2020)'
                    />
                    <textarea
                      maxLength={800}
                      disabled={disabled}
                      name='pepPersonText'
                      placeholder='Enter Details of PEP'
                      onChange={e => handlePartyInputChange(e.target.value, index, 'pepPersonText')}
                      className='w-11/12 mb-3 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                      rows={3}
                    >
                      {`${formData[formName][index]?.pepPersonCountryOrOrganisation || ''}${formData[formName][index]?.pepPersonPublicFunctionNature || ''}${formatDateToString(formData[formName][index]?.pepPersonRoleFrom) || ''}${formatDateToString(formData[formName][index]?.pepPersonRoleTo) || ''}${formData[formName][index]?.pepPersonText || ''}`}
                    </textarea>
                    {/* <label className="block font-medium text-gray-700"> Country/Organisation which PEP holds prominent public function</label>
                          <input maxLength={800} disabled={disabled} type="text" name="pepPersonCountryOrOrganisation"  value={formData[formName][index]?.pepPersonCountryOrOrganisation} placeholder="Enter Country/Organisation " onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepPersonCountryOrOrganisation')}  className="w-full p-1.5 mb-3 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500"/> <br/>
                          <label className="block font-medium text-gray-700"> Nature of prominent public function </label>
                          <input maxLength={800} disabled={disabled} type="text" name="pepPersonPublicFunctionNature" value={formData[formName][index]?.pepPersonPublicFunctionNature}  placeholder="Enter Nature of prominent public function" onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepPersonPublicFunctionNature')}  className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>

                          <label className="font-medium text-gray-700"> From: </label>
                          <input maxLength={800} disabled={disabled} type="date" name="pepPersonRoleFrom" value={formData[formName][index]?.pepPersonRoleFrom} onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepPersonRoleFrom')} id="date" className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none my-1 focus:border-green-500" />
                          <label className="font-medium text-gray-700"> To </label>
                          <input maxLength={800} disabled={disabled} type="date" name="pepPersonRoleTo" value={formData[formName][index]?.pepPersonRoleTo} onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepPersonRoleTo')} id="date" className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" /> */}
                  </div>
                )}
              </div>

              <div className={formData[formName][index].pepAssociate === 'yes' ? 'border p-2' : 'p-2'}>
                <div className='block'>
                  <label className='font-medium text-gray-700 mb-1'>
                    Are you a close associate of any current or former PEP
                    {showRequiredIndicator && <i className='text-red-600 ml-1 mr-1'>*</i>}
                  </label>
                  <Modal
                    title='Definitions'
                    token={token}
                    className='inline'
                    CustomButton={() => <CustomTooltip title='Click to View definition/guidance' />}
                  >
                    <AssociaterPEP />
                  </Modal>
                </div>
                <div className='mt-1 flex items-center'>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='radio'
                    name={'pepAssociate' + index}
                    value='yes'
                    onChange={e => handlePartyInputChange(e.target.value, index, 'pepAssociate')}
                    checked={formData[formName][index]?.pepAssociate === 'yes'}
                    className='mr-1 w-4 h-4'
                  />
                  <label className='mr-2'>Yes</label> <br />
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='radio'
                    name={'pepAssociate' + index}
                    value='no'
                    onChange={e => handlePartyInputChange(e.target.value, index, 'pepAssociate')}
                    checked={formData[formName][index]?.pepAssociate === 'no'}
                    className='mr-1 w-4 h-4'
                  />
                  <label>No</label>
                </div>
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.pepAssociate && (
                  <p style={{ color: 'red' }}>Please choose</p>
                )}

                {formData[formName][index].pepAssociate === 'yes' && (
                  <div className='mt-1 '>
                    <CustomTooltip
                      className='block'
                      title='Please provide details of your relationship with the Politically Exposed Person (PEP), including their full name, official title or role, government/institution affiliation, country of service, period of involvement, and your relationship to them (e.g. spouse, business partner, sibling). e.g. Spouse of current Minister of Finance, Indonesia (2021–present)'
                    />
                    <textarea
                      maxLength={800}
                      disabled={disabled}
                      name='pepAssociateText'
                      placeholder='Enter Details of PEP'
                      onChange={e => handlePartyInputChange(e.target.value, index, 'pepAssociateText')}
                      className='w-11/12 mb-3 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                      rows={3}
                    >
                      {`${formData[formName][index]?.pepAssociateCountryOrOrganisation || ''}${formData[formName][index]?.pepAssociatePublicFunctionNature || ''}${formatDateToString(formData[formName][index]?.pepAssociateRoleFrom) || ''}${formatDateToString(formData[formName][index]?.pepAssociateRoleTo) || ''}${formData[formName][index]?.pepAssociateText || ''}`}
                    </textarea>
                    {/* <p className='font-medium'> Relationship with PEP </p>
                          <input maxLength={800} disabled={disabled} type="text" name="pepAssociateRelationship" value={formData[formName][index]?.pepAssociateRelationship} placeholder="Enter relationship with PEP " onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepAssociateRelationship')}  className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/>
                          <p className='font-medium'> PEP Name </p>
                          <input maxLength={800} disabled={disabled} type="text" name="pepAssociateName" value={formData[formName][index]?.pepAssociateName} placeholder="Enter PEP Name" onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepAssociateName')}  className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/>
                          <p className='font-medium'> Country/Organisation which PEP holds prominent public function </p>
                          <input maxLength={800} disabled={disabled} type="text" name="pepAssociateCountryOrOrganisation" value={formData[formName][index]?.pepAssociateCountryOrOrganisation} placeholder="Enter Country/Organisation " onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepAssociateCountryOrOrganisation')}  className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/>
                          <p className='font-medium'> Nature of prominent public function </p>
                          <input maxLength={800} disabled={disabled} type="text" name="pepAssociatePublicFunctionNature" value={formData[formName][index]?.pepAssociatePublicFunctionNature} placeholder="Enter Nature of prominent public function" onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepAssociatePublicFunctionNature')}  className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/>

                          <span className='font-medium'> From: </span>
                          <input maxLength={800} disabled={disabled} type="date" id="date" name="pepAssociateRoleFrom" value={formData[formName][index]?.pepAssociateRoleFrom} onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepAssociateRoleFrom')} className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" />
                          <span className='font-medium'> To </span>
                          <input maxLength={800} disabled={disabled} type="date" id="date" name="pepAssociateRoleTo" value={formData[formName][index]?.pepAssociateRoleTo} onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepAssociateRoleTo')} className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" /> */}
                  </div>
                )}
              </div>
            </div>
            {/* 10th row */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              <div className={formData[formName][index].pepFamily === 'yes' ? 'border p-2' : 'p-2'}>
                <div className='block'>
                  <label className='font-medium text-gray-700 mb-1'>
                    Are you a family member of any current or former PEP?
                    {showRequiredIndicator && <i className='text-red-600 ml-1 mr-1'>*</i>}
                  </label>
                  <Modal
                    title='Definitions'
                    token={token}
                    className='inline'
                    CustomButton={() => <CustomTooltip title='Click to View definition/guidance' />}
                  >
                    <FamilyMemberPEP />
                  </Modal>
                </div>
                <div className='mt-1 flex items-center'>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='radio'
                    name={'pepFamily' + index}
                    value='yes'
                    onChange={e => handlePartyInputChange(e.target.value, index, 'pepFamily')}
                    checked={formData[formName][index]?.pepFamily === 'yes'}
                    className='mr-1 w-4 h-4'
                  />
                  <label className='mr-2'>Yes</label>
                  <input
                    maxLength={800}
                    disabled={disabled}
                    type='radio'
                    name={'pepFamily' + index}
                    value='no'
                    onChange={e => handlePartyInputChange(e.target.value, index, 'pepFamily')}
                    checked={formData[formName][index]?.pepFamily === 'no'}
                    className='mr-1 w-4 h-4'
                  />
                  <label>No</label>
                </div>
                {showRequiredIndicator && mandatoryConnectedFieldError[index]?.pepFamily && (
                  <p style={{ color: 'red' }}>Please choose</p>
                )}
                {formData[formName][index].pepFamily === 'yes' && (
                  <div className='mt-1 '>
                    <CustomTooltip
                      className='block'
                      title='Please provide details of your relationship with the Politically Exposed Person (PEP), including their full name, official title or role, government/institution affiliation, country of service, period of involvement, and your relationship to them (e.g. spouse, business partner, sibling). e.g. Spouse of current Minister of Finance, Indonesia (2021–present)'
                    />
                    <textarea
                      maxLength={800}
                      disabled={disabled}
                      name='pepFamilyText'
                      placeholder='Enter Details of PEP'
                      onChange={e => handlePartyInputChange(e.target.value, index, 'pepFamilyText')}
                      className='w-11/12 mb-3 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                      rows={3}
                    >
                      {`${formData[formName][index]?.pepFamilyCountryOrOrganisation || ''}${formData[formName][index]?.pepFamilyPublicFunctionNature || ''}${formatDateToString(formData[formName][index]?.pepFamilyRoleFrom) || ''}${formatDateToString(formData[formName][index]?.pepFamilyRoleTo) || ''}${formData[formName][index]?.pepFamilyText || ''}`}
                    </textarea>
                    {/* <p className='font-medium'> Relationship with PEP  </p>
                          <input maxLength={800} disabled={disabled} type="text" name="pepFamilyRelationship" value={formData[formName][index]?.pepFamilyRelationship}  placeholder="Enter relationship with PEP " onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepFamilyRelationship')}  className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>
                          <p className='font-medium'> PEP Name </p>
                          <input maxLength={800} disabled={disabled} type="text" name="pepFamilyName" value={formData[formName][index]?.pepFamilyName}  placeholder="Enter PEP Name" onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepFamilyName')}  className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>

                          <p className='font-medium'> Country/Organisation which PEP holds prominent public function  </p>
                          <input maxLength={800} disabled={disabled} type="text" name="pepFamilyCountryOrOrganisation" value={formData[formName][index]?.pepFamilyCountryOrOrganisation}  placeholder="Enter Country/Organisation " onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepFamilyCountryOrOrganisation')}  className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>
                          <p className='font-medium'>Nature of prominent public function  </p>
                          <input maxLength={800} disabled={disabled} type="text" name="pepFamilyPublicFunctionNature" value={formData[formName][index]?.pepFamilyPublicFunctionNature}  placeholder="Enter Nature of prominent public function" onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepFamilyPublicFunctionNature')}  className="w-full p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500"/> <br/>

                          <span className='font-medium'> From:  </span>
                          <input maxLength={800} disabled={disabled} type="date" id="date" name="pepFamilyRoleFrom"value={formData[formName][index]?.pepFamilyRoleFrom}   onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepFamilyRoleFrom')} className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" />
                          <span className='font-medium'> To  </span>
                          <input maxLength={800} disabled={disabled} type="date" id="date" name="pepFamilyRoleTo" value={formData[formName][index]?.pepFamilyRoleTo}  onChange={(e) => handlePartyInputChange(e.target.value, index, 'pepFamilyRoleTo')} className="w-1/8 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none mb-3 focus:border-green-500" /> */}
                  </div>
                )}
              </div>

              {/* Notes Section */}
              <div>
                <label className='font-medium text-gray-700'>Notes</label>
                <textarea
                  maxLength={800}
                  disabled={disabled}
                  placeholder='Enter Notes'
                  onChange={e => handlePartyInputChange(e.target.value, index, 'notes')}
                  name='notes'
                  value={formData[formName][index]?.notes || ''}
                  className='w-11/12 p-1.5 border-b-2 border-b-gray-300 bg-gray-100 focus:outline-none focus:border-green-500'
                  rows={3}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
const CurrentPEP = () => {
  return (
    <div className='p-6 w-6xl'>
      <p>
        <strong>A Politically Exposed Person (PEP)</strong> refers to someone who is or has been entrusted with a
        prominent public function that includes the role held by a head of state, a head of government, a senior
        politician, a senior government, judicial or military official, a senior executive of a state-owned corporation
        or a senior political party official either domestically or overseas which encompasses of:(a) a foreign
        politically-exposed individual; (b) a domestic politically exposed individual; or (c) an individual who has been
        entrusted with a prominent function in an international organisation. In relation to an international
        organisation, means the role held by a member of the senior management of the international organisation
        (including a director, deputy director or member of a board of the international organisation, or an equivalent
        appointment in the international organisation).
      </p>
    </div>
  );
};

const FamilyMemberPEP = () => {
  return (
    <div className='p-6 w-6xl'>
      <p>
        <strong>A family member</strong> refers to an individual who is a spouse, child (including an adopted child or a
        stepchild), sibling or parent of the politically-exposed individual.
      </p>
    </div>
  );
};

const AssociaterPEP = () => {
  return (
    <div className='p-6 w-6xl'>
      <p>
        <strong>A close associate</strong> refers to an individual who is known to be closely connected to the
        politically-exposed individual, either socially or professionally, such as, but not limited to — (a) a partner
        of the politically exposed individual; (b) an employee or employer of the politically exposed individual; (c) a
        person accustomed or under an obligation, whether formal or informal, to act in accordance with the directions,
        instructions or wishes of the politically exposed individual; or (d) a person whose directions, instructions or
        wishes the politically exposed individual is accustomed or under an obligation, whether formal or informal, to
        act in accordance with.
      </p>
    </div>
  );
};

export default ConnectedOrBeneficialParty;
